import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import type { Asset, Category } from "../../model/asset.model";
import { parsePrimaryTag } from "../../mappers/categoryBrowseUtils";
import { CategoryRow } from "../CategoryRow/CategoryRow";
import { useWatchlist } from "features/watchlist";
import type { Entitlement } from "features/orders";
import {
  SpatialVideoPlayer,
  projectionModeForFormat,
} from "shared/components/SpatialVideoPlayer";
import { useRealCatalog } from "../../hooks/useRealCatalog";
import { LicenseCheckoutModal } from "./LicenseCheckoutModal";
import styles from "./SingleTitleView.module.css";

export const DEMO_PREVIEW_SRC = "/demo-stream/quima-preview.mp4";

/** Display model for the single-title hero; maps cleanly from `Asset` + extras when wired to API. */
export type SingleTitleHeroData = {
  title: string;
  creatorStudio: string;
  category: string;
  format: string;
  duration: string;
  description: string;
  backgroundImageUrl: string;
};

export type SingleTitleViewProps = {
  /** Shown in “You might also like”; defaults to real catalog minus current title */
  recommendedAssets?: Asset[];
  onBack?: () => void;
  onWatchFree?: () => void;
  /** Sticky header primary CTA; falls back to `onWatchFree` if omitted */
  onBuy?: () => void;
  /** Label for sticky “Buy” button (default matches Figma-style copy) */
  buyLabel?: string;
  onRecommendTagSearch?: (label: string) => void;
  className?: string;
};
/** Default recommendations when no external prop is provided. */
export const SINGLE_TITLE_RECOMMENDED_DUMMY: Asset[] = [];

const StudioIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const BookmarkIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
  </svg>
);

const BookmarkFilledIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
  </svg>
);

/** Default hero payload for development / Storybook until wired to routing. */
export const SINGLE_TITLE_VIEW_DUMMY: SingleTitleHeroData = {
  title: "Silent Echoes",
  creatorStudio: "Tsukikage Films (月影フィルム)",
  category: "Category",
  format: "Format",
  duration: "27 min.",
  description:
    "In a city that never sleeps, sound is currency. A field recordist discovers frequencies that rewrite memory—and must choose between preserving the past or silencing it forever. An atmospheric journey through urban liminal spaces with spatial audio designed for immersive playback.",
  backgroundImageUrl:
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2000&q=80",
};

/** When no player callback is wired, scroll the main column (matches layout `.main-content`). */
function scrollMainContentToTop() {
  const main = document.querySelector(".main-content");
  if (main instanceof HTMLElement) {
    main.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function assetToHeroData(asset: Asset, categories: Category[]): SingleTitleHeroData {
  const { primary, rest } = parsePrimaryTag(asset.tags);
  const catalogCategory = categories.find((c) =>
    c.assets.some((a) => a.id === asset.id)
  )?.name;

  return {
    title: asset.title,
    creatorStudio: catalogCategory ?? primary,
    category: primary,
    format: rest.trim() || "—",
    duration: asset.duration,
    description: asset.description,
    backgroundImageUrl: asset.thumbnailUrl,
  };
}

export function SingleTitleView({
  recommendedAssets: recommendedAssetsProp,
  onBack,
  onWatchFree,
  onBuy,
  buyLabel = "Buy €13.99",
  onRecommendTagSearch,
  className,
}: SingleTitleViewProps) {
  const { titleId } = useParams<{ titleId: string }>();
  const navigate = useNavigate();
  const watchlist = useWatchlist();
  const { assets, categories } = useRealCatalog();

  const asset = useMemo(
    () => (titleId ? assets.find((a) => a.id === titleId) : undefined),
    [assets, titleId]
  );

  const recommendedAssets = useMemo(() => {
    if (!asset) return [];
    const pool =
      recommendedAssetsProp ?? assets;
    return pool.filter((a) => a.id !== asset.id);
  }, [recommendedAssetsProp, assets, asset]);

  const alsoLikeCategory = useMemo<Category>(
    () => ({
      id: "also-like",
      name: "You might also like",
      assets: recommendedAssets,
    }),
    [recommendedAssets]
  );

  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const [stickyHeaderVisible, setStickyHeaderVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [licensed, setLicensed] = useState<Entitlement | null>(null);

  useEffect(() => {
    setPlaying(false);
    setLicenseModalOpen(false);
    setLicensed(null);
  }, [titleId]);

  /**
   * Show the frosted bar when the hero H1 leaves the viewport.
   * IntersectionObserver is tied to the layout viewport (not window.scrollY), so it behaves
   * the same at all breakpoints. Portaling the header to document.body avoids it being
   * clipped by .main-content { overflow: hidden }.
   */
  useEffect(() => {
    const el = heroTitleRef.current;
    if (!el || !titleId) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        setStickyHeaderVisible(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [titleId]);

  const goBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleWatchFreeClick = () => {
    if (onWatchFree) {
      onWatchFree();
      return;
    }
    scrollMainContentToTop();
    setPlaying(true);
  };

  const handleBuyClick = () => {
    if (onBuy) {
      onBuy();
      return;
    }
    setLicenseModalOpen(true);
  };

  const closeLicenseModal = useCallback(() => setLicenseModalOpen(false), []);

  const handleLicensed = useCallback((entitlement: Entitlement) => {
    setLicensed(entitlement);
    setLicenseModalOpen(false);
    scrollMainContentToTop();
    setPlaying(true);
  }, []);

  if (!titleId || !asset) {
    return (
      <div className={[styles.notFoundRoot, className].filter(Boolean).join(" ")}>
        <p className={styles.notFoundText}>Title not found</p>
        <button
          type="button"
          className={styles.notFoundBack}
          onClick={goBack}
        >
          Go back
        </button>
      </div>
    );
  }

  const data = assetToHeroData(asset, categories);
  const {
    title,
    creatorStudio,
    category,
    format,
    duration,
    description,
    backgroundImageUrl,
  } = data;

  const metaLine = [category, format, duration].filter(Boolean).join(" • ");

  const saved =
    watchlist.enabled && watchlist.isInWatchlist(asset.id);
  const watchlistLabel = saved
    ? "Remove from watchlist"
    : "Add to watchlist";
  const handleWatchlistClick = () => {
    void watchlist.toggleWatchlist(asset);
  };

  const stickyHeaderEl = (
    <header
      className={[
        styles.stickyHeader,
        stickyHeaderVisible ? styles.stickyHeaderVisible : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden={!stickyHeaderVisible}
    >
      <div className={styles.stickyHeaderInner}>
        <div className={styles.stickyLeft}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={goBack}
            aria-label="Go back"
          >
            <ChevronLeftIcon />
          </button>
        </div>
        <p className={styles.stickyTitle} aria-hidden>
          {title}
        </p>
        <div className={styles.stickyRight}>
          <button
            type="button"
            className={styles.stickyBuyBtn}
            onClick={handleBuyClick}
          >
            {buyLabel}
          </button>
          {watchlist.enabled ? (
            <button
              type="button"
              className={[
                styles.stickyWatchlistBtn,
                saved ? styles.stickyWatchlistBtnActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={handleWatchlistClick}
              aria-label={watchlistLabel}
            >
              {saved ? <BookmarkFilledIcon /> : <BookmarkIcon />}
              <span>{watchlistLabel}</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );

  return (
    <>
      {typeof document !== "undefined"
        ? createPortal(stickyHeaderEl, document.body)
        : null}
      <div className={[styles.root, className].filter(Boolean).join(" ")}>
      <section
        className={styles.hero}
        aria-labelledby="single-title-heading"
      >
        <div
          className={styles.heroBg}
          style={{ backgroundImage: `url(${backgroundImageUrl})` }}
          role="img"
          aria-hidden
        />
        <div className={styles.heroGradient} aria-hidden />

        <div className={styles.heroTopBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={goBack}
            aria-label="Go back"
          >
            <ChevronLeftIcon />
          </button>
          {watchlist.enabled ? (
            <button
              type="button"
              className={[
                styles.watchlistBtn,
                saved ? styles.watchlistBtnActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={handleWatchlistClick}
              aria-label={watchlistLabel}
            >
              {saved ? <BookmarkFilledIcon /> : <BookmarkIcon />}
              <span>{watchlistLabel}</span>
            </button>
          ) : null}
        </div>

        <div className={styles.heroBody}>
          <div className={styles.heroTextBlock}>
            <div className={styles.creatorBadge}>
              <span className={styles.creatorIcon} aria-hidden>
                <StudioIcon />
              </span>
              <span>{creatorStudio}</span>
            </div>

            <h1
              id="single-title-heading"
              ref={heroTitleRef}
              className={styles.title}
            >
              {title}
            </h1>

            <p className={styles.meta}>{metaLine}</p>

            <p className={styles.description}>{description}</p>

            <div className={styles.ctaRow}>
              <button
                type="button"
                className={styles.watchPrimary}
                onClick={handleWatchFreeClick}
                aria-label="Watch for free"
              >
                Watch for free
              </button>
              <button
                type="button"
                className={styles.licenseBtn}
                onClick={handleBuyClick}
                aria-haspopup="dialog"
                aria-expanded={licenseModalOpen}
              >
                License / Buy
              </button>
              {licensed ? (
                <span
                  className={styles.licensedBadge}
                  data-testid="licensed-badge"
                  role="status"
                >
                  <span className={styles.licensedCheck}>Licensed ✓</span>
                  <span className={styles.licensedTxn}>
                    Transaction {licensed.transactionId}
                  </span>
                </span>
              ) : null}
            </div>
            {playing ? (
              <SpatialVideoPlayer
                src={DEMO_PREVIEW_SRC}
                title={title}
                defaultMode={projectionModeForFormat(`${format} ${asset.tags}`)}
              />
            ) : null}
          </div>
        </div>
      </section>

      <LicenseCheckoutModal
        open={licenseModalOpen}
        title={title}
        assetUploadId={asset.assetUploadId}
        priceLabel={asset.price}
        onClose={closeLicenseModal}
        onLicensed={handleLicensed}
      />

      {recommendedAssets.length > 0 ? (
        <div className={styles.alsoLike}>
          <CategoryRow
            className={styles.alsoLikeRow}
            category={alsoLikeCategory}
            hideViewAll
            onPrimaryTagSearch={onRecommendTagSearch}
          />
        </div>
      ) : null}
    </div>
    </>
  );
}
